const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const WEEK_OF_MONTH_INDEX = {
  First: 0,
  Second: 1,
  Third: 2,
  Fourth: 3,
};

export function parseDateIsoLocal(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!ISO_DATE_RE.test(trimmed)) return null;

  const [year, month, day] = trimmed.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function startOfLocalDay(dateLike) {
  const parsed =
    dateLike instanceof Date
      ? new Date(dateLike)
      : parseDateIsoLocal(dateLike);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function isAfterRecurrenceEnd(dateLike, recurrenceEndDate) {
  const occurrenceDay = startOfLocalDay(dateLike);
  const recurrenceEndDay = startOfLocalDay(recurrenceEndDate);

  if (!occurrenceDay || !recurrenceEndDay) return false;
  return occurrenceDay > recurrenceEndDay;
}

export function getWeeklyRecurrenceScanLimit({
  now = new Date(),
  recurrenceEndDate = '',
  fallbackOccurrences = 24,
}) {
  const recurrenceEndDay = startOfLocalDay(recurrenceEndDate);
  if (!recurrenceEndDay) return fallbackOccurrences;

  const safeNow = now instanceof Date ? new Date(now) : new Date();
  if (Number.isNaN(safeNow.getTime())) return fallbackOccurrences;

  const diffMs = recurrenceEndDay.getTime() - safeNow.getTime();
  if (diffMs <= 0) return fallbackOccurrences;

  const weeksUntilEnd = Math.ceil(diffMs / WEEK_MS) + 1;
  return Math.max(fallbackOccurrences, weeksUntilEnd);
}

function monthlyOccurrence(year, monthIndex, dayIndex, weekOfMonth) {
  if (weekOfMonth === 'Last') {
    const candidate = new Date(year, monthIndex + 1, 0);
    candidate.setDate(candidate.getDate() - ((7 + candidate.getDay() - dayIndex) % 7));
    return candidate;
  }

  const candidate = new Date(year, monthIndex, 1);
  const offset = (7 + dayIndex - candidate.getDay()) % 7;
  const weekIndex = WEEK_OF_MONTH_INDEX[weekOfMonth] ?? 0;
  candidate.setDate(1 + offset + weekIndex * 7);
  return candidate;
}

/**
 * Build occurrence dates for one recurrence day without losing monthly rules.
 * Dates begin on the local calendar day containing `now`; callers can exclude
 * an occurrence whose time has already passed when their surface requires it.
 */
export function getRecurrenceDatesForDay({
  now = new Date(),
  dayCode,
  recurrence = {},
  maxOccurrences = 12,
}) {
  let start = now instanceof Date ? new Date(now) : new Date();
  const anchor = parseDateIsoLocal(recurrence.startDate);
  if (anchor && anchor > start) start = new Date(anchor);
  if (Number.isNaN(start.getTime())) return [];

  const dayIndex = DAY_CODES.indexOf(dayCode);
  if (dayIndex < 0) return [];

  const safeLimit = Math.max(0, Math.floor(Number(maxOccurrences) || 0));
  if (!safeLimit) return [];

  const [rawHour, rawMinute] = String(recurrence.time || '00:00').split(':').map(Number);
  const hour = Number.isFinite(rawHour) ? rawHour : 0;
  const minute = Number.isFinite(rawMinute) ? rawMinute : 0;
  const frequency = String(recurrence.frequency || 'WEEKLY').toUpperCase();
  const interval = Math.max(1, Math.floor(Number(recurrence.interval) || 1));
  const dates = [];

  if (frequency === 'MONTHLY') {
    const startDay = startOfLocalDay(start);
    let monthOffset = 0;
    while (dates.length < safeLimit && monthOffset <= (safeLimit + 1) * interval) {
      const monthCursor = new Date(start.getFullYear(), start.getMonth() + monthOffset, 1);
      const candidate = monthlyOccurrence(
        monthCursor.getFullYear(),
        monthCursor.getMonth(),
        dayIndex,
        recurrence.weekOfMonth || 'First'
      );
      candidate.setHours(hour, minute, 0, 0);
      monthOffset += 1;

      if (anchor && ((monthCursor.getFullYear() - anchor.getFullYear()) * 12 + monthCursor.getMonth() - anchor.getMonth()) % interval !== 0) continue;
      if (startOfLocalDay(candidate) < startDay) continue;
      if (isAfterRecurrenceEnd(candidate, recurrence.endDate)) break;
      dates.push(candidate);
    }
    return dates;
  }

  const first = new Date(start);
  const offset = (7 + dayIndex - first.getDay()) % 7;
  first.setDate(start.getDate() + offset);
  first.setHours(hour, minute, 0, 0);

  if (anchor && interval > 1) {
    const monday = (date) => {
      const day = new Date(date);
      day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
      return Date.UTC(day.getFullYear(), day.getMonth(), day.getDate());
    };
    const weeks = Math.round((monday(first) - monday(anchor)) / WEEK_MS);
    first.setDate(first.getDate() + ((interval - weeks % interval) % interval) * 7);
  }
  for (let index = 0; index < safeLimit; index += 1) {
    const candidate = new Date(first);
    candidate.setDate(first.getDate() + index * 7 * interval);
    if (isAfterRecurrenceEnd(candidate, recurrence.endDate)) break;
    dates.push(candidate);
  }
  return dates;
}
